'use client';

import type { KeyboardEvent } from 'react';
import type { UserQuestDto } from '@/queries/userQuest/userQuest.schema';
import { Icon } from '@/components/entities/Sprite';
import { useMarketFiatCurrency } from '@/hooks/useMarketFiatCurrency';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { formatCentsToCurrencyNumber, formatCompactNumber, getFiatCurrencyPrefix } from '@/utils/currency.utils';
import { calculateProgressPercent } from '@/utils/progress.utils';
import { GmProgressBar } from '../GmProgressBar';
import { getMissionTitleParts } from './utils/missionTitle.utils';

interface Props extends UserQuestDto {
  onAction?: VoidFunction;
  href?: string;
}

const MIN_FILL_VIEW_PROGRESS_BAR = 5;
/** Placeholder in a mission name replaced with the player's market currency symbol (`$` / `C$`). */
const CURRENCY_PLACEHOLDER = '{{CUR}}';

export const GmMissionCard = ({ onAction, href, ...mission }: Props) => {
  const { currency } = useMarketFiatCurrency();
  const isSumTotalProgress = mission.quest.progress.type === 'sum';
  const done = isSumTotalProgress
    ? formatCentsToCurrencyNumber(mission.progress)
    : mission.progress ?? 0;
  const total = (isSumTotalProgress ? formatCentsToCurrencyNumber : (val: number) => val)(
    mission.quest.progress.target ?? 0,
  );
  const percentageProgress = calculateProgressPercent(done, total);
  const title = mission.quest.name.replaceAll(CURRENCY_PLACEHOLDER, getFiatCurrencyPrefix(currency));
  const titleParts = getMissionTitleParts(title);
  const xp = mission.quest.xpAmount ?? 0;

  const isInteractive = !!onAction || !!href;
  const missionsProgress = Math.min(percentageProgress, 100);
  const missionCompleted = !!mission.completedAt;
  const missionPercentProgress = Math.max(missionsProgress, done ? MIN_FILL_VIEW_PROGRESS_BAR : 0);

  const handleMissionKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onAction?.();
    }
  };

  const className = cn(
    // TODO: need confirmation with design, cause h-fit make weird view
    'rounded-2xl glass-stroke-border box-border shadow-card gm-mission-gradient w-full',
    'lg:gap-4 gap-0 py-4 min-h-22 lg:py-4 flex justify-between items-center pl-4 pr-6 lg:pr-4',
    isInteractive ? 'lg:pr-4 pr-1 cursor-pointer' : 'lg:pr-10 pr-6',
    missionCompleted && 'opacity-50',
  );

  const content = (
    <>
      <div className="flex flex-col gap-3 pr-2 grow">
        <p className="text-sm text-start font-normal leading-5 text-light">
          {titleParts.map(({ text, highlighted }, index) => highlighted
            ? <span key={index} className="gm-text-golden-3 font-semibold!">{text}</span>
            : text)}
        </p>

        <div className="relative">
          <GmProgressBar
            className="**:data-[slot=progressBarTrack]:z-10"
            showLightEffect={false}
            percent={missionPercentProgress}
          />

          <p className="text-c300 font-semibold leading-[0.813rem] absolute lg:mr-px right-2 top-0 text-[0.688rem]">
            {`${done}/${total}`}
          </p>
        </div>
      </div>

      <div className="flex justify-center items-center">
        <div className="flex relative gm-mission-xp-wrapper-border gm-mission-xp-shadow font-semibold flex-col justify-center items-center rounded-full min-w-12.5 min-h-12.5 bg-main-800/40 px-2">
          <p className="text-lg leading-4.5 tracking-tighter gm-mission-xp-bg">
            {formatCompactNumber(xp, { minUnit: 'thousand' })}
          </p>
          <p className="text-sm leading-3 gm-mission-xp-bg">XP</p>
        </div>

        {isInteractive && (
          <Icon name="arrow-pagination" className="size-5 lg:size-6 text-light rotate-180" />
        )}
      </div>
    </>
  );

  if (href) {
    return (
      <Link href={href} onClick={onAction} className={className}>
        {content}
      </Link>
    );
  }

  if (onAction) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onAction}
        onKeyDown={handleMissionKeyDown}
        className={className}
      >
        {content}
      </div>
    );
  }

  return <div className={className}>{content}</div>;
};
